import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Home,
  Settings,
  LogOut,
  User,
  Globe,
  BarChart3,
  ArrowLeft,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIE_COLORS = ["#D4AF37", "#0B1F3B", "#64748B", "#10B981"];

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/analytics/business`);
      setAnalytics(res.data);
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const fmtCurrency = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Destructure overview safely
  const overview = analytics?.overview || {};
  const totalEst = overview.total_estimates || 0;
  const winRate = overview.win_rate || 0;
  const totalRevenue = overview.total_revenue || 0;
  const avgRating = overview.avg_rating || 0;
  const totalReviews = overview.total_reviews || 0;
  const avgEstimate = overview.avg_estimate_amount || 0;
  const acceptedEst = overview.accepted_estimates || 0;
  const rejectedEst = overview.rejected_estimates || 0;
  const pendingEst = overview.pending_estimates || 0;

  const responseTimes = analytics?.response_times || {};
  const avgResponse = responseTimes.avg_hours || 0;
  const fastestResponse = responseTimes.fastest_hours || 0;
  const slowestResponse = responseTimes.slowest_hours || 0;

  // Format monthly data for chart (show short month names)
  const monthlyRaw = analytics?.monthly_revenue || [];
  const monthlyData = monthlyRaw.map((item) => {
    const mStr = item.month || "";
    const rev = item.revenue || 0;
    const sent = item.estimates_sent || 0;
    const won = item.estimates_won || 0;
    const parts = mStr.split("-");
    const yr = parts[0] || "";
    const mo = parts[1] || "";
    const shortMonth = mo
      ? new Date(Number(yr), Number(mo) - 1).toLocaleString("en-US", { month: "short" })
      : mStr;
    return { name: shortMonth, revenue: rev, sent: sent, won: won };
  });

  // Pie chart data for estimate status
  const pieData = [
    { name: t("analytics.won"), value: acceptedEst },
    { name: t("analytics.pending"), value: pendingEst },
    { name: t("analytics.lost"), value: rejectedEst },
  ].filter((d) => d.value > 0);

  // Category breakdown
  const categoryData = (analytics?.category_breakdown || []).map((c) => {
    const catName = c.category || "Other";
    const catEst = c.estimates || 0;
    const catWon = c.won || 0;
    const catRev = c.revenue || 0;
    return { name: catName, estimates: catEst, won: catWon, revenue: catRev };
  });

  const hasData = totalEst > 0;

  // Sidebar user info — destructure to avoid chained access
  const userName = user?.name || "";
  const userPicture = user?.picture || "";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar - Desktop */}
      <aside className="dashboard-sidebar hidden md:block">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#0B1F3B]" />
            </div>
            <span className="text-xl font-extrabold text-white" style={{ fontFamily: "Manrope" }}>
              MyProjectBidder
            </span>
          </div>
          <nav className="space-y-2">
            <Link
              to="/business"
              className="sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white"
            >
              <Home className="w-5 h-5" />
              Dashboard
            </Link>
            <Link
              to="/business/analytics"
              className="sidebar-link active flex items-center gap-3 px-4 py-3 rounded-lg text-white"
            >
              <BarChart3 className="w-5 h-5" />
              {t("analytics.title")}
            </Link>
            <Link
              to="/business/profile"
              className="sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white"
            >
              <Settings className="w-5 h-5" />
              {t("nav.businessProfile")}
            </Link>
          </nav>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {userPicture ? (
                <img src={userPicture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                </div>
              )}
              <div className="overflow-hidden">
                <p className="text-white font-medium text-sm truncate">{userName}</p>
              </div>
            </div>
            <NotificationBell />
          </div>
          <Link
            to="/settings"
            className="sidebar-link flex items-center gap-3 px-4 py-3 mb-2 rounded-lg text-slate-300 hover:text-white"
          >
            <Globe className="w-5 h-5" />
            {t("common.settings")}
          </Link>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
            data-testid="analytics-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("common.signOut")}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-[#0B1F3B] text-white p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/business")} className="text-white hover:bg-white/10 p-1">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="font-bold">{t("analytics.title")}</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-content p-4 md:p-8" data-testid="analytics-dashboard">
        <div className="mb-6">
          <h1 className="text-xl md:text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: "Manrope" }}>
            {t("analytics.title")}
          </h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">{t("analytics.subtitle")}</p>
        </div>

        {!hasData ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="analytics-empty">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-bold text-[#0B1F3B] mb-2">{t("analytics.noData")}</h3>
            <p className="text-slate-500 text-sm">{t("analytics.noDataDesc")}</p>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="kpi-grid">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="kpi-win-rate">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{t("analytics.winRate")}</p>
                <p className={`text-2xl md:text-3xl font-extrabold ${winRate >= 50 ? "text-emerald-600" : winRate >= 25 ? "text-[#D4AF37]" : "text-rose-500"}`} style={{ fontFamily: "Manrope" }}>
                  {winRate}%
                </p>
                <p className="text-xs text-slate-400 mt-1">{acceptedEst} {t("analytics.wonOf")} {totalEst}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="kpi-total-revenue">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{t("analytics.totalRevenue")}</p>
                <p className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: "Manrope" }}>
                  {fmtCurrency(totalRevenue)}
                </p>
                <p className="text-xs text-slate-400 mt-1">{t("analytics.avgBid")}: {fmtCurrency(avgEstimate)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="kpi-avg-rating">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{t("analytics.avgRating")}</p>
                <p className={`text-2xl md:text-3xl font-extrabold ${avgRating >= 4 ? "text-emerald-600" : "text-[#0B1F3B]"}`} style={{ fontFamily: "Manrope" }}>
                  {avgRating > 0 ? `${avgRating} / 5` : "\u2014"}
                </p>
                <p className="text-xs text-slate-400 mt-1">{totalReviews > 0 ? `${totalReviews} ${t("analytics.reviews")}` : t("analytics.noReviewsYet")}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="kpi-avg-response-time">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{t("analytics.avgResponseTime")}</p>
                <p className={`text-2xl md:text-3xl font-extrabold ${avgResponse > 0 && avgResponse <= 12 ? "text-emerald-600" : "text-[#0B1F3B]"}`} style={{ fontFamily: "Manrope" }}>
                  {avgResponse > 0 ? `${avgResponse}h` : "\u2014"}
                </p>
                <p className="text-xs text-slate-400 mt-1">{avgResponse > 0 ? `${t("analytics.fastest")}: ${fastestResponse}h \u00B7 ${t("analytics.slowest")}: ${slowestResponse}h` : t("analytics.noResponseData")}</p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
              {/* Revenue Trend (takes 2 cols) */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="revenue-chart">
                <h2 className="text-sm font-bold text-[#0B1F3B] uppercase tracking-wide mb-4">
                  {t("analytics.revenueTrend")}
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                      <Tooltip
                        formatter={(val, name) => {
                          if (name === "revenue") return [`$${Number(val).toLocaleString()}`, "Revenue"];
                          if (name === "sent") return [val, "Bids Sent"];
                          if (name === "won") return [val, "Bids Won"];
                          return [val, name];
                        }}
                        contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                      />
                      <Bar dataKey="revenue" fill="#D4AF37" radius={[4, 4, 0, 0]} name="revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Estimate Status Pie */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="status-pie-chart">
                <h2 className="text-sm font-bold text-[#0B1F3B] uppercase tracking-wide mb-4">
                  {t("analytics.bidOutcomes")}
                </h2>
                {pieData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {pieData.map((entry, idx) => {
                            const entryName = entry.name;
                            const color = PIE_COLORS[idx % PIE_COLORS.length];
                            return <Cell key={entryName} fill={color} />;
                          })}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-12">{t("analytics.noData")}</p>
                )}
              </div>
            </div>

            {/* Bids Over Time (Line Chart) */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 mb-8" data-testid="bids-line-chart">
              <h2 className="text-sm font-bold text-[#0B1F3B] uppercase tracking-wide mb-4">
                {t("analytics.bidsOverTime")}
              </h2>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="sent" stroke="#0B1F3B" strokeWidth={2} name={t("analytics.bidsSent")} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="won" stroke="#D4AF37" strokeWidth={2} name={t("analytics.bidsWon")} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category Breakdown Table */}
            {categoryData.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="category-table">
                <div className="p-5 border-b border-slate-100">
                  <h2 className="text-sm font-bold text-[#0B1F3B] uppercase tracking-wide">
                    {t("analytics.categoryBreakdown")}
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left">
                        <th className="px-5 py-3 font-semibold text-slate-600">{t("analytics.category")}</th>
                        <th className="px-5 py-3 font-semibold text-slate-600 text-center">{t("analytics.bidsSent")}</th>
                        <th className="px-5 py-3 font-semibold text-slate-600 text-center">{t("analytics.bidsWon")}</th>
                        <th className="px-5 py-3 font-semibold text-slate-600 text-center">{t("analytics.winRateShort")}</th>
                        <th className="px-5 py-3 font-semibold text-slate-600 text-right">{t("analytics.revenue")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {categoryData.map((row) => {
                        const rowName = row.name;
                        const rowEst = row.estimates;
                        const rowWon = row.won;
                        const rowRev = row.revenue;
                        const rowWR = rowEst > 0 ? Math.round((rowWon / rowEst) * 100) : 0;
                        return (
                          <tr key={rowName} className="hover:bg-slate-50 transition-colors">
                            <td className="px-5 py-3 font-medium text-[#0B1F3B]">{rowName}</td>
                            <td className="px-5 py-3 text-center text-slate-600">{rowEst}</td>
                            <td className="px-5 py-3 text-center text-slate-600">{rowWon}</td>
                            <td className="px-5 py-3 text-center">
                              <span
                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                                  rowWR >= 50 ? "bg-emerald-100 text-emerald-700" : rowWR >= 25 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                                }`}
                              >
                                {rowWR}%
                              </span>
                            </td>
                            <td className="px-5 py-3 text-right font-semibold text-[#0B1F3B]">{fmtCurrency(rowRev)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
